import React, {
  memo, useEffect, useState, useContext, useCallback,
} from 'react';
import { useQuery } from 'react-query';
import { useMediaQuery } from 'react-responsive';
import { useParams } from 'react-router';
import moment from 'moment';

import DetailHeader from '../components/DetailHeader';
import {
  parameterConfigurations,
  getExternalObsDatasets,
  getExternalParameters,
  getExternalObsLatestData,
  getExternalObs,
} from '../../../services/dataset.service';
import { getOrganizations } from '../../../services/organization.service';
import MapQueryTypes from '../../../services/query-types/map';
import {
  getExternalParameterObservations,
} from '../../../utils';
import './styles.scss';
import MarkerStatus from '../../../components/icons/MarkerStatus';
import UserContext from '../../../contexts/UserContext';
import appConfig, { brandingConfig } from '../../../config';
import ExternalParametersList from './ExternalParametersList';
import { dateRanges } from '../constant';

const ExternalConsoleDetail = () => {
  const { id } = useParams();

  const isMobile = useMediaQuery({ maxWidth: 767 });

  const [organization, setOrganization] = useState();

  const { unitPreferences } = useContext(UserContext);

  const {
    data: parameterConfigurationsResult,
    isLoading: parameterConfigurationsIsLoading,
  } = useQuery(MapQueryTypes.REST_PARAMETER_CONFIGURATIONS, parameterConfigurations, { refetchOnWindowFocus: false });

  const {
    data: externalObsDatasets,
    isLoading: externalObsDatasetsIsLoading,
  } = useQuery(MapQueryTypes.REST_EXTERNAL_OBS_DATASETS, getExternalObsDatasets, { refetchOnWindowFocus: false });

  const {
    data: externalParameters,
    isLoading: externalParametersIsLoading,
  } = useQuery(MapQueryTypes.REST_EXTERNAL_PARAMETERS, getExternalParameters, { refetchOnWindowFocus: false });

  const {
    data: externalObsLatest,
    isLoading: externalObsLatestIsLoading,
  } = useQuery(MapQueryTypes.REST_EXTERNAL_OBS_LATEST_DATA, getExternalObsLatestData, { refetchOnWindowFocus: false });

  const obsDataset = externalObsDatasets?.find((o) => o.ext_obs_dataset_id === Number(id));

  const {
    data: organizationsResult,
  } = useQuery(MapQueryTypes.REST_ORGANIZATIONS, getOrganizations, { refetchOnWindowFocus: false });

  const filterValue = {
    extObsDatasetIds: [Number(id)],
    range: dateRanges[0].value,
  };

  const {
    data: externalObs,
    // isLoading: externalObsIsLoading,
  } = useQuery([MapQueryTypes.REST_EXTERNAL_OBS, filterValue], () => getExternalObs(filterValue));

  // const {
  //   data: platformMetadataResult,
  //   refetch: refetchPlatformMetadata,
  // } = useQuery([MapQueryTypes.REST_PLATFORM_METADATA, datasetIds],
  //   () => getPlatformMetadata(datasetIds[0]),
  //   {
  //     refetchOnWindowFocus: false,
  //     enabled: !!datasetIds.length,
  //   });

  // useEffect(() => {
  //   if (obsDatasetSummariesResult) {
  //     if (isPlatformsDataConsole) {
  //       setObsDataset(obsDatasetSummariesResult.find((o) => !!o.obs_dataset_id));
  //       setDatasetIds(obsDatasetSummariesResult.map((s) => s.obs_dataset_id));
  //     } else {
  //       setObsDataset(obsDatasetSummariesResult.find((o) => datasetIds.includes(o.obs_dataset_id)));
  //     }
  //   }
  // }, [obsDatasetSummariesResult]);

  // useEffect(() => {
  //   let date;
  //   let latestDatasetIds;
  //   if (obsLatestDataResult && obsDataset) {
  //     latestDatasetIds = obsLatestDataResult.map((o) => o.obs_dataset_id).filter((o) => datasetIds.includes(o));
  //     // setDatasetIdsLatest(latestDatasetIds);
  //     date = getLatestParameterDate(obsDataset, obsLatestDataResult, true);

  //     setHeaderInfo({
  //       last_updated_parameter_in_hours: moment().diff(moment(new Date(date)), 'hours'),
  //       platform_event: obsDataset.obs_dataset_platform_assignment?.platform?.platform_event?.event,
  //       organization_id: obsDataset.obs_dataset_platform_assignment?.platform?.organization_id,
  //     });
  //   }
  //   if (!dataStartDate && obsDataset && date) {
  //     if (isPlatformsDataConsole) {
  //       latestDatasetIds.forEach((id) => {
  //         const datasetObsDate = getLatestParameterDate(obsDatasetSummariesResult.find((o) => o.obs_dataset_id === id), obsLatestDataResult, true);
  //         if (datasetObsDate < date) {
  //           date = datasetObsDate;
  //         }
  //       });
  //     }
  //     const dateParam = date ? moment(date).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD');
  //     setDataStartDate(dateParam);
  //   }
  // }, [obsLatestDataResult, obsDataset]);

  // useEffect(() => {
  //   if (!!datasetIds.length && !platformMetadataResult) {
  //     refetchPlatformMetadata();
  //   }
  // }, [datasetIds, platformMetadataResult]);

  const externalParameterObs = getExternalParameterObservations(
    obsDataset,
    externalParameters,
    externalObsLatest,
    parameterConfigurationsResult,
    unitPreferences,
  );

  useEffect(() => {
    if (obsDataset && organizationsResult && organizationsResult.length > 0) {
      const organization = organizationsResult.find((o) => o.organization_id === obsDataset.organization_id);
      setOrganization(organization);
    }
  }, [obsDataset, organizationsResult]);

  const getChartData = useCallback((parameter) => {
    const chartData = [];
    const parameterObs = externalObs?.parameter_obs?.values?.[parameter.ext_parameter_id];

    if (!parameterObs) {
      return chartData;
    }

    parameterObs.forEach((o, i) => {
      chartData.push([externalObs.timestamps[i], o]);
    });

    return chartData;
  }, [externalObs]);

  const breadcrumb = [
    {
      name: obsDataset?.platform_name,
      href: `/data-console/external/${obsDataset?.ext_obs_dataset_id}`,
    },
  ];

  let lastUpdatedInHours = Infinity;

  if (externalObsLatest?.length > 0) {
    const extObs = externalObsLatest.find(
      (o) => o.ext_obs_dataset_id === obsDataset?.ext_obs_dataset_id,
    );

    if (extObs && extObs.parameters?.length > 0) {
      const allTimestamps = extObs.parameters.flatMap((p) => p.observations?.map((obs) => obs.timestamp) || []);

      if (allTimestamps.length > 0) {
        const latestTimestamp = Math.max(...allTimestamps);
        lastUpdatedInHours = moment().diff(
          moment.unix(latestTimestamp),
          'hours',
        );
      }
    }
  }

  const platformEvent = lastUpdatedInHours <= appConfig.oldDataCutoffInHours ? 'activated' : 'unavailable';

  const headerInfo = {
    last_updated_parameter_in_hours: lastUpdatedInHours,
    organization_id: obsDataset?.organization_id,
    platform_event: platformEvent,
    platform_type: obsDataset?.platform_type,
  };

  return (
    <div className="console-detail">
      <DetailHeader
        breadcrumb={breadcrumb}
        dataset={obsDataset}
        isMobile={isMobile}
        organization={organization}
        isPlatformsDataConsole={false}
        statusEl={(
          obsDataset && (
            <MarkerStatus
              properties={headerInfo}
              iconProps={{
                height: headerInfo?.organization_id === brandingConfig.filters.organizationId ? '16px' : '24px',
                width: headerInfo?.organization_id === brandingConfig.filters.organizationId ? '16px' : '24px',
              }}
            />
          )
        )}
      />
      <ExternalParametersList
        platform={obsDataset}
        parameters={externalParameterObs}
        isMobile={isMobile}
        onGetChartData={getChartData}
        isLoading={
          parameterConfigurationsIsLoading
          || externalObsDatasetsIsLoading
          || externalParametersIsLoading
          || parameterConfigurationsIsLoading
          || externalObsLatestIsLoading
        }
      />
    </div>
  );
};

ExternalConsoleDetail.propTypes = {};

ExternalConsoleDetail.defaultProps = {};

export default memo(ExternalConsoleDetail);
